import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Landing from './pages/Landing';
import PropertyList from './pages/PropertyList';
import PropertyDetail from './pages/PropertyDetail';
import BookingFlow from './pages/BookingFlow';
import HostDashboard from './pages/HostDashboard';
import TravelerDashboard from './pages/TravelerDashboard';
import AuthCallback from './pages/AuthCallback';
import Login from './pages/Login';
import SupplierOnboarding from './pages/SupplierOnboarding';
import SupplierDashboard from './pages/SupplierDashboard';
import CreateStayListing from './pages/CreateStayListing';
import CreateTrekListing from './pages/CreateTrekListing';
import CreateEventListing from './pages/CreateEventListing';
// New Pages
import AdminDataPreview from './pages/AdminDataPreview';
import AdminReview from './pages/AdminReview';
import AdminTeaser from './pages/AdminTeaser';
import FoundersLinks from './pages/FoundersLinks';
import Apply from './pages/Apply';
import ApplyThanks from './pages/ApplyThanks';
import StaysListing from './pages/StaysListing';
import StayDetail from './pages/StayDetail';
import TreksListing from './pages/TreksListing';
import EventsListing from './pages/EventsListing';
import TravelerLogin from './pages/TravelerLogin';
import BookingConfirmation from './pages/BookingConfirmation';
import About from './pages/About';
import HostLanding from './pages/HostLanding';
import InvestorLanding from './pages/InvestorLanding';
import DiscoveredStays from './pages/DiscoveredStays';
import CollectiveStay from './pages/CollectiveStay';
import ValleyPage from './pages/ValleyPage';
import FeaturedStay from './pages/FeaturedStay';
import CollectionLanding from './pages/CollectionLanding';
import AdminCollections from './pages/AdminCollections';
import CorporateOhsm from './pages/CorporateOhsm';
import HostOffer from './pages/HostOffer';
import FoundersNote from './pages/FoundersNote';
import LongerRoad from './pages/LongerRoad';
import LongerRoadPoster from './pages/LongerRoadPoster';
import SocialGenerator from './pages/SocialGenerator';
import Wishlist from './pages/Wishlist';
import SampleVitals from './pages/SampleVitals';
import PitchHimachal from './pages/PitchHimachal';
import PitchUttarakhand from './pages/PitchUttarakhand';
import PitchMeghalaya from './pages/PitchMeghalaya';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import EditorialPolicy from './pages/EditorialPolicy';
import QR from './pages/QR';
import QRStarterPack from './pages/QRStarterPack';
import Itineraries from './pages/Itineraries';
import ItineraryDetail from './pages/ItineraryDetail';
import TripPlanner from './pages/TripPlanner';
import RESpiti from './pages/RESpiti';
import PitchBrands from './pages/PitchBrands';
import PitchSonipatDrive from './pages/PitchSonipatDrive';
import Impact from './pages/Impact';
import Store from './pages/Store';
import Links from './pages/Links';
import IHGPitch from './pages/IHGPitch';
import StrategicPitch from './pages/StrategicPitch';
import BusinessPlan from './pages/BusinessPlan';
import AdminLeads from './pages/AdminLeads';
import AdminLaunchKit from './pages/AdminLaunchKit';
import AdminOutreach from './pages/AdminOutreach';
import Retail from './pages/Retail';
import VisionPitch from './pages/VisionPitch';
import HimachalStatePage from './pages/HimachalStatePage';
import UttarakhandStatePage from './pages/UttarakhandStatePage';
import JKStatePage from './pages/JKStatePage';
import LadakhStatePage from './pages/LadakhStatePage';
import SikkimStatePage from './pages/SikkimStatePage';
import ArunachalStatePage from './pages/ArunachalStatePage';
import WestBengalStatePage from './pages/WestBengalStatePage';
import AssamStatePage from './pages/AssamStatePage';
import MeghalayaStatePage from './pages/MeghalayaStatePage';
import NagalandStatePage from './pages/NagalandStatePage';
import ManipurStatePage from './pages/ManipurStatePage';
import MizoramStatePage from './pages/MizoramStatePage';
import TripuraStatePage from './pages/TripuraStatePage';
import StateAnalytics from './pages/StateAnalytics';
import ProtectedRoute from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/properties" element={<PropertyList />} />
        <Route path="/properties/:propertyId" element={<PropertyDetail />} />
        <Route path="/booking/:propertyId" element={<ProtectedRoute><BookingFlow /></ProtectedRoute>} />
        <Route path="/host-dashboard" element={<ProtectedRoute><HostDashboard /></ProtectedRoute>} />
        <Route path="/traveler-dashboard" element={<ProtectedRoute><TravelerDashboard /></ProtectedRoute>} />
        
        {/* Supplier/Host Routes */}
        <Route path="/supplier/onboarding" element={<SupplierOnboarding />} />
        <Route path="/supplier/dashboard" element={<SupplierDashboard />} />
        <Route path="/supplier/listings/stays/new" element={<CreateStayListing />} />
        <Route path="/supplier/listings/treks/new" element={<CreateTrekListing />} />
        <Route path="/supplier/listings/events/new" element={<CreateEventListing />} />
        <Route path="/host" element={<HostLanding />} />
        <Route path="/partner" element={<HostLanding />} />
        
        {/* Admin Routes */}
        {/* /admin → legacy SaaS dashboard (deprecated). Redirect to the live admin home. */}
        <Route path="/admin" element={<Navigate to="/admin/review" replace />} />
        <Route path="/admin/review" element={<AdminReview />} />
        <Route path="/admin/teaser" element={<AdminTeaser />} />
        <Route path="/admin/curate" element={<Navigate to="/admin/review" replace />} />
        {/* Internal directory of every active stay with host details + CTAs */}
        <Route path="/directory" element={<FoundersLinks />} />
        <Route path="/apply" element={<Apply />} />
        <Route path="/apply/thanks/:id" element={<ApplyThanks />} />
        {/* Legacy: /admin/data is now a tab inside /admin/review */}
        <Route path="/admin/data" element={<Navigate to="/admin/review?tab=valleys" replace />} />
        
        {/* Public Traveler Routes */}
        <Route path="/stays" element={<StaysListing />} />
        <Route path="/stays/:listingId" element={<StayDetail />} />
        <Route path="/treks" element={<TreksListing />} />
        <Route path="/events" element={<EventsListing />} />
        
        {/* Traveler Auth & Booking */}
        <Route path="/traveler/login" element={<TravelerLogin />} />
        <Route path="/booking/confirmation" element={<BookingConfirmation />} />
        
        {/* About Page */}
        <Route path="/about" element={<About />} />
        
        {/* Investor Page */}
        <Route path="/invest" element={<InvestorLanding />} />
        
        {/* Discovered Stays */}
        <Route path="/discovered" element={<DiscoveredStays />} />
        <Route path="/collective/:slug" element={<CollectiveStay />} />
        <Route path="/valley/:slug" element={<ValleyPage />} />
        <Route path="/stay/:slug" element={<FeaturedStay />} />
        <Route path="/collection/:slug" element={<CollectionLanding />} />
        <Route path="/admin/collections" element={<AdminCollections />} />
        <Route path="/admin/social" element={<SocialGenerator />} />
        <Route path="/wishlist" element={<Wishlist />} />
        <Route path="/pitch/himachal" element={<PitchHimachal />} />
        <Route path="/pitch/uttarakhand" element={<PitchUttarakhand />} />
        <Route path="/pitch/meghalaya" element={<PitchMeghalaya />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/editorial-policy" element={<EditorialPolicy />} />
        <Route path="/qr" element={<QR />} />
        <Route path="/qr/starter-pack" element={<QRStarterPack />} />
        <Route path="/itineraries" element={<Itineraries />} />
        <Route path="/itinerary/:slug" element={<ItineraryDetail />} />
        <Route path="/plan" element={<TripPlanner />} />
        <Route path="/adventures/royal-enfield-spiti" element={<RESpiti />} />
        <Route path="/pitch/brands" element={<PitchBrands />} />
        <Route path="/pitch/sonipat-drive" element={<PitchSonipatDrive />} />
        <Route path="/impact" element={<Impact />} />
        <Route path="/store" element={<Store />} />
        <Route path="/sample/vitals" element={<SampleVitals />} />
        <Route path="/links" element={<Links />} />
        <Route path="/links/ihg-pitch" element={<IHGPitch />} />
        <Route path="/links/strategic-pitch" element={<StrategicPitch />} />
        <Route path="/business-plan" element={<BusinessPlan />} />
        <Route path="/corporate/ohsm" element={<CorporateOhsm />} />
        <Route path="/host-offer" element={<HostOffer />} />
        <Route path="/founders-note" element={<FoundersNote />} />
        <Route path="/itineraries/longer-road" element={<LongerRoad />} />
        <Route path="/itineraries/longer-road/poster" element={<LongerRoadPoster />} />
        <Route path="/admin/leads" element={<AdminLeads />} />
        <Route path="/admin/launch-kit" element={<AdminLaunchKit />} />
        <Route path="/admin/outreach" element={<AdminOutreach />} />
        <Route path="/retail" element={<Retail />} />
        <Route path="/pitch/vision-himachal" element={<VisionPitch stateSlug="himachal" />} />
        <Route path="/pitch/vision-uttarakhand" element={<VisionPitch stateSlug="uttarakhand" />} />
        <Route path="/pitch/vision-meghalaya" element={<VisionPitch stateSlug="meghalaya" />} />
        
        {/* State Partnership Pages */}
        <Route path="/states/himachal" element={<HimachalStatePage />} />
        <Route path="/states/uttarakhand" element={<UttarakhandStatePage />} />
        <Route path="/states/jk" element={<JKStatePage />} />
        <Route path="/states/kashmir" element={<JKStatePage />} />
        <Route path="/states/ladakh" element={<LadakhStatePage />} />
        <Route path="/states/sikkim" element={<SikkimStatePage />} />
        <Route path="/states/arunachal" element={<ArunachalStatePage />} />
        <Route path="/states/westbengal" element={<WestBengalStatePage />} />
        <Route path="/states/assam" element={<AssamStatePage />} />
        <Route path="/states/meghalaya" element={<MeghalayaStatePage />} />
        <Route path="/states/nagaland" element={<NagalandStatePage />} />
        <Route path="/states/manipur" element={<ManipurStatePage />} />
        <Route path="/states/mizoram" element={<MizoramStatePage />} />
        <Route path="/states/tripura" element={<TripuraStatePage />} />
        <Route path="/states/analytics" element={<StateAnalytics />} />
        {/* Legacy redirect */}
        <Route path="/for-states/himachal" element={<HimachalStatePage />} />
      </Routes>
      <Toaster position="top-right" />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
